import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionProducts } from '@/hooks/subscription-products';
import Navigation from '@/components/Navigation';
import Footer from '@/components/ui/footer';
import { SubscriptionProductCard } from '@/components/subscription/SubscriptionProductCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Store, Sparkles, Palette, Users, HardDrive, Gift, Building, Code, Package } from 'lucide-react';
import type { SubscriptionProduct } from '@shared/schema';

const CATEGORIES = [
  { value: 'all', label: 'All', icon: Store },
  { value: 'premium', label: 'Premium', icon: Sparkles },
  { value: 'character_studio', label: 'Character Studio', icon: Palette },
  { value: 'child_profiles', label: 'Child Profiles', icon: Users },
  { value: 'illustrations', label: 'Illustrations', icon: Palette },
  { value: 'storage', label: 'Storage', icon: HardDrive },
  { value: 'gift_cards', label: 'Gift Cards', icon: Gift },
  { value: 'white_label', label: 'White Label', icon: Building },
  { value: 'api', label: 'API', icon: Code },
] as const;

function filterByCategory(products: SubscriptionProduct[], category: string): SubscriptionProduct[] {
  if (category === 'all') {
    return products;
  }
  return products.filter((product) => {
    const metadata = product.metadata || {};
    return metadata.category === category;
  });
}

export default function SubscriptionStore() {
  const { user, isLoading: authLoading } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  
  const { data, isLoading, error } = useSubscriptionProducts();
  
  const allProducts = data?.products || [];
  const filteredProducts = filterByCategory(allProducts, activeCategory);
  const activeProducts = filteredProducts.filter((p) => p.isActive);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Navigation />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl" id="main-content">
        <div className="text-center mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent mb-4"
            data-testid="store-title"
          >
            Subscription Store
          </h1>
          <p 
            className="text-muted-foreground max-w-2xl mx-auto"
            data-testid="store-description"
          >
            Explore our plans and add-ons to enhance your storytelling experience. 
            From premium features to custom characters, find the perfect fit for your family.
          </p>
        </div>

        <Tabs 
          value={activeCategory} 
          onValueChange={setActiveCategory}
          className="w-full"
        >
          <div className="flex justify-center mb-8">
            <TabsList className="flex flex-wrap justify-center gap-1 h-auto p-1 bg-muted/50">
              {CATEGORIES.map(({ value, label, icon: Icon }) => (
                <TabsTrigger 
                  key={value} 
                  value={value}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-teal-600 data-[state=active]:text-white"
                  data-testid={`tab-category-${value}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {CATEGORIES.map(({ value }) => (
            <TabsContent key={value} value={value} className="mt-0">
              {isLoading ? (
                <div 
                  className="flex flex-col items-center justify-center py-16"
                  data-testid="store-loading"
                >
                  <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-4" />
                  <p className="text-muted-foreground">Loading products...</p>
                </div>
              ) : error ? (
                <div 
                  className="flex flex-col items-center justify-center py-16"
                  data-testid="store-error"
                >
                  <Package className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Failed to load products. Please try again later.</p>
                </div>
              ) : activeProducts.length === 0 ? (
                <div 
                  className="flex flex-col items-center justify-center py-16"
                  data-testid="store-empty"
                >
                  <Package className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {value === 'all' 
                      ? 'No products available at this time.'
                      : `No ${CATEGORIES.find(c => c.value === value)?.label || value} products available.`
                    }
                  </p>
                </div>
              ) : (
                <div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  data-testid="store-products-grid"
                >
                  {activeProducts.map((product) => (
                    <SubscriptionProductCard
                      key={product.id}
                      product={product}
                      data-testid={`store-product-${product.id}`}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
